import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import OrderSummary from './components/OrderSummary';
import PaymentMethodSelector from './components/PaymentMethodSelector';
import PaymentForm from './components/PaymentForm';
import TransactionStatus from './components/TransactionStatus';
import SecurityIndicators from './components/SecurityIndicators';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const PaymentProcessing = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [transactionStatus, setTransactionStatus] = useState('form'); // form, processing, success, failed, pending
  const [transactionData, setTransactionData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [walletBalance] = useState(45000); // Mock wallet balance

  // Mock order data - in real app, this would come from props/context
  const [orderData] = useState({
    orderId: `ORD${Date.now()}`,
    items: [
      {
        title: 'Premium Listing - Toyota Camry 2018',
        description: 'Featured listing with top positioning for 30 days',
        amount: 5000,
        duration: '30 days'
      },
      {
        title: 'Geographic Visibility - Douala Region',
        description: 'Extended visibility across Douala and surrounding areas',
        amount: 3500,
        originalAmount: 4000,
        duration: '1 month'
      }
    ]
  });

  const [discounts] = useState([
    {
      code: 'NEWUSER20',
      description: 'New user discount',
      amount: 500
    }
  ]);

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    // Parse URL parameters for order details
    const orderId = searchParams?.get('order');
    const amount = searchParams?.get('amount');
    
    if (orderId || amount) {
      // In real app, fetch order details from API
      console.log('Loading order:', orderId, amount);
    }
  }, [navigate, searchParams]);

  const handlePaymentMethodSelect = (method) => {
    setSelectedPaymentMethod(method);
  };

  const handlePaymentSubmit = async (paymentData) => {
    setIsProcessing(true);
    setTransactionStatus('processing');

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Mock transaction data
      const transaction = {
        id: `TXN${Date.now()}`,
        amount: calculateTotal(),
        method: selectedPaymentMethod?.name,
        timestamp: new Date()?.toISOString(),
        status: 'completed'
      };

      // Simulate random success/failure for demo
      const success = Math.random() > 0.15; // 85% success rate

      if (success) {
        setTransactionData(transaction);
        setTransactionStatus('success');
        
        // Update user's subscription/listing status
        updateUserServices();
      } else {
        setTransactionStatus('failed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setTransactionStatus('failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const updateUserServices = () => {
    // In real app, update user's active subscriptions and premium features
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    userData.premiumFeatures = {
      ...userData?.premiumFeatures,
      featuredListings: true,
      extendedVisibility: true,
      activatedAt: new Date()?.toISOString()
    };
    localStorage.setItem('userData', JSON.stringify(userData));
  };

  const calculateTotal = () => {
    const subtotal = orderData?.items?.reduce((sum, item) => sum + item?.amount, 0);
    const discountAmount = discounts?.reduce((sum, discount) => sum + discount?.amount, 0);
    return subtotal - discountAmount;
  };

  const handleRetryPayment = () => {
    setTransactionStatus('form');
    setTransactionData(null);
    setSelectedPaymentMethod(null);
  };

  const handleCancelPayment = () => {
    navigate('/subscription-management');
  };

  const handleWhatsAppReceipt = () => {
    const message = `AutoCasse Payment Receipt\n\nTransaction ID: ${transactionData?.id}\nAmount: ${new Intl.NumberFormat('fr-CM', { style: 'currency', currency: 'XAF' })?.format(transactionData?.amount || 0)}\nDate: ${new Date()?.toLocaleDateString('fr-CM')}\n\nThank you for your payment!`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/subscription-management')}
              iconName="ArrowLeft"
              iconSize={16}
            />
            <div>
              <h1 className="text-2xl font-bold text-foreground">Payment Processing</h1>
              <p className="text-muted-foreground">Complete your secure payment transaction</p>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="flex items-center space-x-2 mb-6">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              transactionStatus === 'form' ? 'bg-primary text-primary-foreground' : 'bg-success text-success-foreground'
            }`}>
              <Icon name={transactionStatus === 'form' ? 'CreditCard' : 'Check'} size={16} />
            </div>
            <div className={`h-1 w-16 ${
              ['processing', 'success', 'failed', 'pending']?.includes(transactionStatus) ? 'bg-success' : 'bg-border'
            }`}></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              ['processing', 'success', 'failed', 'pending']?.includes(transactionStatus) 
                ? transactionStatus === 'success' ? 'bg-success text-success-foreground' : 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground'
            }`}>
              <Icon name={
                transactionStatus === 'success' ? 'Check' : 
                transactionStatus === 'processing' ? 'Loader2' : 
                transactionStatus === 'failed' ? 'X' : 'Clock'
              } size={16} className={transactionStatus === 'processing' ? 'animate-spin' : ''} />
            </div>
            <div className={`h-1 w-16 ${
              transactionStatus === 'success' ? 'bg-success' : 'bg-border'
            }`}></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              transactionStatus === 'success' ? 'bg-success text-success-foreground' : 'bg-muted text-muted-foreground'
            }`}>
              <Icon name="CheckCircle" size={16} />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Payment Form */}
          <div className="lg:col-span-2 space-y-6">
            {transactionStatus === 'form' && (
              <>
                <PaymentMethodSelector
                  selectedMethod={selectedPaymentMethod}
                  onMethodSelect={handlePaymentMethodSelect}
                  walletBalance={walletBalance}
                />
                
                <PaymentForm
                  paymentMethod={selectedPaymentMethod}
                  onSubmit={handlePaymentSubmit}
                  isProcessing={isProcessing}
                />
              </>
            )}

            {['processing', 'success', 'failed', 'pending']?.includes(transactionStatus) && (
              <TransactionStatus
                status={transactionStatus}
                transactionData={transactionData}
                onRetry={handleRetryPayment}
                onCancel={handleCancelPayment}
                onWhatsAppReceipt={handleWhatsAppReceipt}
              />
            )}
          </div>

          {/* Right Column - Order Summary & Security */}
          <div className="space-y-6">
            <OrderSummary
              orderData={orderData}
              discounts={discounts}
            />
            
            <SecurityIndicators />

            {/* Quick Actions */}
            <div className="bg-card border border-border rounded-lg p-4 shadow-soft">
              <h3 className="font-medium text-foreground mb-3">Need Help?</h3>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  iconName="MessageCircle"
                  iconPosition="left"
                  iconSize={16}
                  onClick={() => window.open('https://wa.me/237123456789', '_blank')}
                >
                  WhatsApp Support
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  fullWidth
                  iconName="HelpCircle"
                  iconPosition="left"
                  iconSize={16}
                  onClick={() => navigate('/help')}
                >
                  Payment FAQ
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Bottom Actions */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border p-4 shadow-floating">
          {transactionStatus === 'form' && selectedPaymentMethod && (
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-muted-foreground">Total Amount</div>
                <div className="font-bold text-lg text-primary">
                  {new Intl.NumberFormat('fr-CM', { 
                    style: 'currency', 
                    currency: 'XAF',
                    minimumFractionDigits: 0 
                  })?.format(calculateTotal())}
                </div>
              </div>
              <Button
                variant="default"
                iconName="Lock"
                iconPosition="left"
                iconSize={16}
                disabled={!selectedPaymentMethod}
              >
                Pay Now
              </Button>
            </div>
          )}
        </div>

        {/* Add bottom padding for mobile */}
        <div className="lg:hidden h-20"></div>
      </div>
    </div>
  );
};

export default PaymentProcessing;